import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { PatternFormat } from 'react-number-format'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Button, Spinner, Alert } from 'react-bootstrap'
import { sendOTPForUser, resendOTPForUser, verifyOTPForEmail } from 'api/shipping'
import { setEmailAddress } from 'store/shipping/shippingSlice'
import styled from 'styled-components'
import { breakpoints, colors } from 'globalStyles'
import GuestButton from 'components/GuestButton'

const StyledVerifyContent = styled.div`
  font-family: Open Sans;
  line-height: 1.15;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(17, 17, 17, 0);
  text-align: center;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;

  margin-top: 84px;
  margin-bottom: 84px;
  @media ${breakpoints.sm_above} {
    margin-top: 80px;
    margin-bottom: 60px;
  }
  @media ${breakpoints.lg_above} {
    margin-top: 76px;
    margin-bottom: 60px;
  }

  form {
    text-align: left;
    margin: 0 auto;
  }

  label,
  .get-estimates-form label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  .form-group {
    margin-top: 24px;
  }

  .alert {
    color: #e00000;
    padding: 1rem;
  }

  input[type='text'],
  input[type='tel'] {
    border-radius: 5px;
    border-width: 1px;
    border-color: ${colors.secondary_grey};
    padding-left: 16px;
    height: 3rem;
    text-align: left !important;
    width: 100%;
  }

  #OptInLabel {
    font-size: 0.8rem;
    position: relative;
  }
  .concents-checkbox {
    vertical-align: middle;
    margin-right: 0.5rem;
  }

  .form-control {
    padding: 0.5rem;
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 1.25rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  .btn-primary {
    width: 100%;
    color: white;
    background-color: #001996;
    border-color: #001996;
  }

  .btn-secondary {
    width: 100%;
    color: #001996;
    background-color: #ffffff;
    border: 1px solid #001996;
    margin-top: 10px;
  }

  .btn-secondary:hover {
    background-color: #ffffff;
    color: #0a38e6;
    border: 1px solid #0a38e6;
  }

  .btn-secondary:not(:disabled):not(.disabled):active {
    color: #001996;
    background-color: #ffffff;
    border: 2px solid #001996;
  }

  [type='submit']:not(:disabled) {
    cursor: pointer;
    -webkit-appearance: button;
  }
  .form-copy-error {
    color: #f00;
    border-color: #f00 !important;
  }
  .cta-box-col-2 div {
    max-width: 300px;
  }

  #firstname-error,
  #lastname-error,
  #phone-error,
  #optin-error {
    text-align: left;
    font-size: 11px;
    color: red;
  }
  .contacterror {
    color: #ff0004;
  }
  .contactnormal {
    color: #000000;
  }
  @media only screen and (min-width: 992px) {
    .cta-box-col-2 div {
      margin-left: 50px;
    }
  }
`
const StyledBackground = styled.div`
  display: flex;
  flex-direction: column;

  .body {
    padding-top: 10%;
    padding-bottom: 5%;
    height: 100%;
  }
  @media screen and ${breakpoints.tablet} {
    .body {
      padding-left: 19%;
      padding-right: 19%;
      padding-top: 10%;
      padding-bottom: 5%;
      height: 100%;
    }
  }
  @media screen and ${breakpoints.desktop} {
    .body {
      padding-left: 28%;
      padding-right: 28%;
      padding-top: 10%;
      padding-bottom: 5%;
      height: 100%;
    }
  }
`
const Heading = styled.h4`
  width: 100%;
  text-align: center;
  display: block;
  font-size: 28px;
  line-height: 38px;
  color: ${colors.primary_blue};
  @media screen and ${breakpoints.tablet} {
    display: block;
    font-size: 32px;
    line-height: 45px;
    margin-bottom: 20px;
  }
`

const RegisterLink = styled.a.attrs({
  id: 'register-link',
  rel: 'noreferrer',
})`
  color: ${colors.primary_blue};
`

const StyledButtonContainer = styled.div`
  @media ${breakpoints.tablet_above} {
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 20px;
  }
`

// use error codes to retrieve relevant error message strings

// TODO: the error messages might be replaced depending on other tickets
const getVerificationCodeErrorMessage = (errorStatus, t) => {
  switch (errorStatus) {
    case 2:
      return t('subscription.verification_code_invalid_response')
    case 5:
      return t('forgotemail.verification_code_expired_error')
    case 6:
      return t('subscription.verification_code_max_attempts_error')
    default:
      return t('subscription.general_error_message')
  }
}
const getAlertMessage = (t, errorStatus) => {
  switch (errorStatus) {
    case 1:
      return t('subscription.general_error_message')
    case 4:
      return t('subscription.phone_number_invalid_error')
    default:
      return null
  }
}

const ForgotEmailForm = () => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // 0: not submitted, 1: loading, 2: submitted, 3: verifying, 4: success
  const [formStep, setFormStep] = useState(0)
  // 0: no error, 1: send verification code error, 2: verify verification code error,
  // 3: subscribe error, 4: invalid phone number, 5: expired verification code, 6: maximum verification code attempts reached
  // 7: unregistered error
  const [errorStatus, setErrorStatus] = useState(0)
  const [sendAgainDisabled, setSendAgainDisabled] = useState(false)
  const [verifyDisabled, setVerifyDisabled] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [prevLang, setPrevLang] = useState(i18n.language)

  const linkContainerStyle = {
    justifyContent: 'center',
    alignItems: 'center',
    margin: '10px 0',
    padding: '10px 0',
    fontSize: '16px',
    textAlign: 'center',
    lineHeight: '24px',
  }

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitted },
    trigger,
    reset,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    if (sendAgainDisabled) {
      const zeroAttemptsTimeout = setTimeout(() => {
        setSendAgainDisabled(false)
        setErrorStatus(0)
      }, 1000 * 30)

      return () => {
        clearTimeout(zeroAttemptsTimeout)
      }
    }

    if (verifyDisabled) {
      const maxAttemptsTimeout = setTimeout(() => {
        setVerifyDisabled(false)
        setErrorStatus(0)
      }, 1000 * 30)

      return () => {
        clearTimeout(maxAttemptsTimeout)
      }
    }

    if (prevLang !== language) {
      if (isSubmitted) {
        trigger()
      }
      setPrevLang(language)
    }
    return null
  }, [sendAgainDisabled, verifyDisabled, prevLang, language, trigger, isSubmitted])

  const formatNumber = (num) => {
    return num.replace(/[^\d]/g, '')
  }
  const displayFormatNumber = (num) => {
    const cleaned = num.replace(/[^\d]/g, '')
    // format the phone number to display with * except the last 3 digits
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.slice(0, -3).replace(/\d/g, '*') + cleaned.slice(-3)
    }
    return cleaned
  }

  const onClickRegister = (e) => {
    e.preventDefault()
    navigate(`/rs/tr/${language}/register`, {
      replace: true,
    })
  }

  const sendCode = (values) => {
    // Template syntax follows url-template https://www.npmjs.com/package/url-template
    const phonenumber = formatNumber(values.phoneNumber)
    setPhoneNumber(phonenumber)

    setFormStep(1)
    sendOTPForUser(phonenumber, language)
      .then((result) => {
        // TODO: uncomment the conditions and remove console log for production (commented for testing purposes only)
        if (
          result.data.status.success === 'true' &&
          (result.data.data[0].response === 'SUCCESS' ||
            result.data.data[0].response === 'VERIFICATION_CODE_ALREADY_EXISTS')
        ) {
          setFormStep(2)
          setErrorStatus(0)
          reset()
          return true
        }
        if (result.data.data[0].response === 'PHONE_IS_NOT_MOBILE') {
          setErrorStatus(4)
          setFormStep(0)
          return true
        }
        setErrorStatus(1)
        setFormStep(0)
        return true
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to connect to API', err)
        setErrorStatus(1)
        setFormStep(0)
      })
  }
  // Resend OTP Logic reused from RegistrationForm.jsx
  const resendCodeHandler = () => {
    setSendAgainDisabled(true)
    resendOTPForUser(formatNumber(phoneNumber), language)
      .then((result) => {
        if (result.status === 200 && typeof result.data.data !== 'undefined') {
          if (
            result.data.data.length > 0 &&
            result.data.data[0].response === 'MAX_VERIFICATIONCODE_RESEND_LIMIT'
          ) {
            setErrorStatus(6)
            setFormStep(2)
            setSendAgainDisabled(true)
            return true
          }
        }
        return true
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to connect to API', err)
      })
  }

  const verifyCode = async (values) => {
    try {
      const otpEmailResp = await verifyOTPForEmail(values.verificationCode, phoneNumber, language)
      dispatch(setEmailAddress(otpEmailResp.data.email))
      navigate(`/rs/tr/${language}/login`, {
        replace: true,
      })
    } catch (error) {
      const responseCode = error?.response?.data?.error[0]?.response
      if (responseCode === 'VERIFICATION_CODE_DOES_NOT_EXISTS') {
        setErrorStatus(5)
        setFormStep(2)
      } else {
        setErrorStatus(2)
        setFormStep(2)
      }
    }
  }

  const submitForm = async (fields) => {
    switch (formStep) {
      case 0:
        sendCode(fields)
        break
      case 2:
        verifyCode(fields)
        break
      default:
        break
    }
  }
  const goBack = (e) => {
    e.preventDefault()
    navigate(`/rs/tr/${language}/login`, {
      replace: true,
    })
  }
  return (
    <StyledBackground>
      <div className="body flex-grow-1 align-items-center justify-content-center">
        <StyledVerifyContent>
          <Heading className="heading">{t('forgotemail.title')}</Heading>
          <form onSubmit={handleSubmit(submitForm)} style={{ paddingTop: '20px' }}>
            {(formStep === 0 || formStep === 1) && (
              <>
                <p style={{ textAlign: 'center' }}>{t('forgotemail.heading')}</p>
                <div className="form-group">
                  <Controller
                    control={control}
                    name="phoneNumber"
                    defaultValue=""
                    rules={{
                      required: {
                        value: true,
                        message: t('subscription.phone_number_invalid_error'),
                      },
                      validate: (value) => {
                        const onlyNumbers = formatNumber(value) // Remove non-digit characters
                        if (onlyNumbers.length !== 11) {
                          return t('subscription.phone_number_invalid_error')
                        }
                        return true
                      },
                    }}
                    render={({ field: { name, value, ref, onChange } }) => (
                      <PatternFormat
                        format="1 ### ### ####"
                        displayType="input"
                        type="tel"
                        name={name}
                        value={value}
                        getInputRef={ref}
                        onChange={onChange}
                        placeholder={t('forgotemail.placeholder')}
                        style={{ paddingLeft: '10px' }}
                      />
                    )}
                  />
                  <span role="alert" id="phone-error">
                    {errors.phoneNumber?.message}
                  </span>
                </div>
                <div style={{ fontSize: '13px', padding: '0px' }}>
                  {errorStatus !== 0 && (
                    <Alert
                      variant="light"
                      style={{ paddingTop: '0px', paddingBottom: '0px', marginBottom: '0px' }}
                    >
                      {getAlertMessage(t, errorStatus)}
                    </Alert>
                  )}
                </div>
              </>
            )}
            {(formStep === 0 || formStep === 1) && (
              <StyledButtonContainer>
                <Button onClick={goBack} className="btn-secondary">
                  {t('forgotemail.go_back')}
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={verifyDisabled}
                  style={{ marginTop: '10px' }}
                >
                  {formStep === 0 ? (
                    t('buttonActions.submit')
                  ) : (
                    <Spinner
                      as="span"
                      animation="border"
                      size="md"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                </Button>
              </StyledButtonContainer>
            )}
            {(formStep === 2 || formStep === 3) && (
              <>
                <div className="row" style={{ paddingTop: '20px' }}>
                  <div className="col" style={{ textAlign: 'center' }}>
                    <Trans
                      t={t}
                      i18nKey="forgotemail.verificationTitle"
                      values={{ phoneNumber: displayFormatNumber(phoneNumber) }}
                      components={[<b />]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="form-group">
                    <input
                      type="text"
                      name="verificationCode"
                      aria-invalid={errors.verificationCode ? 'true' : 'false'}
                      placeholder={t('forgotemail.verificationPlaceholder')}
                      {...register('verificationCode', {
                        required: {
                          value: true,
                          message: t('subscription.verification_code_empty_error'),
                        },
                        pattern: {
                          value: /^[0-9]*$/,
                          message: t('subscription.verification_code_invalid_error'),
                        },
                        maxLength: {
                          value: 6,
                          message: t('subscription.verification_code_invalid_error'),
                        },
                        minLength: {
                          value: 6,
                          message: t('subscription.verification_code_invalid_error'),
                        },
                      })}
                    />
                    <div id="firstname-error">{errors.verificationCode?.message}</div>
                  </div>
                </div>
                <div className="row text-center" style={{ paddingTop: '10px' }}>
                  <div className="col">
                    <p className="small">
                      <Trans
                        t={t}
                        i18nKey="subscription.no_sms_received"
                        components={[
                          <Button
                            variant="link"
                            onClick={resendCodeHandler}
                            disabled={sendAgainDisabled}
                          />,
                        ]}
                      />
                    </p>
                  </div>
                </div>
                {errorStatus !== 0 ? (
                  <Alert variant="light">{getVerificationCodeErrorMessage(errorStatus, t)}</Alert>
                ) : null}
              </>
            )}
            {(formStep === 2 || formStep === 3) && (
              <Button type="submit" className="btn-primary" disabled={verifyDisabled}>
                {formStep === 2 ? (
                  t('subscription.verify_button')
                ) : (
                  <Spinner
                    as="span"
                    animation="border"
                    size="md"
                    role="status"
                    aria-hidden="true"
                  />
                )}
              </Button>
            )}
            <div style={linkContainerStyle}>
              <Trans
                t={t}
                i18nKey="forgotemail.no_account"
                components={[<RegisterLink onClick={onClickRegister} />]}
              />
            </div>
            <div style={linkContainerStyle}>
              <GuestButton isSrcLandingPage={false} />
            </div>
          </form>
        </StyledVerifyContent>
      </div>
    </StyledBackground>
  )
}

export default ForgotEmailForm
